import { gsap } from './gsapSetup';

/**
 * Lightweight, accessible text splitting (no paid SplitText plugin).
 * The original text is preserved as an aria-label on the host; visual spans are aria-hidden,
 * so screen readers and crawlers still read the real, unbroken text.
 */

export interface SplitResult {
  targets: HTMLElement[];
  revert: () => void;
}

/**
 * Run `cb` once web fonts are ready, so line-break measurement in splitToLines uses the real
 * font metrics (avoids clipped headings from the fallback-font FOUT race). Returns a canceller.
 */
export function whenFontsReady(cb: () => void): () => void {
  let cancelled = false;
  const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
  if (fonts && fonts.status !== 'loaded') {
    fonts.ready.then(() => {
      if (!cancelled) cb();
    });
  } else {
    cb();
  }
  return () => {
    cancelled = true;
  };
}

function storeOriginal(el: HTMLElement) {
  if (!el.dataset.origHtml) el.dataset.origHtml = el.innerHTML;
  if (!el.getAttribute('aria-label')) el.setAttribute('aria-label', el.textContent?.trim() ?? '');
}

export function splitToWords(el: HTMLElement): SplitResult {
  storeOriginal(el);
  const words = (el.textContent ?? '').split(/(\s+)/);
  el.innerHTML = '';
  const inner: HTMLElement[] = [];
  for (const w of words) {
    if (w.trim() === '') {
      el.appendChild(document.createTextNode(w));
      continue;
    }
    const outer = document.createElement('span');
    outer.className = 'split-word';
    outer.setAttribute('aria-hidden', 'true');
    const span = document.createElement('span');
    span.textContent = w;
    outer.appendChild(span);
    el.appendChild(outer);
    inner.push(span);
  }
  return { targets: inner, revert: () => revert(el) };
}

export function splitToLines(el: HTMLElement): SplitResult {
  storeOriginal(el);
  // First split to words to measure line breaks.
  const { targets: words } = splitToWords(el);
  const lines: HTMLElement[][] = [];
  let lastTop: number | null = null;
  for (const word of words) {
    const outer = word.parentElement as HTMLElement;
    const top = outer.offsetTop;
    if (lastTop === null || Math.abs(top - lastTop) > 2) {
      lines.push([]);
      lastTop = top;
    }
    lines[lines.length - 1].push(outer);
  }
  // Rebuild wrapping each line in an overflow-hidden block.
  el.innerHTML = '';
  const inner: HTMLElement[] = [];
  for (const lineWords of lines) {
    const lineWrap = document.createElement('span');
    lineWrap.className = 'split-line';
    lineWrap.setAttribute('aria-hidden', 'true');
    const lineInner = document.createElement('span');
    lineWords.forEach((wordOuter, i) => {
      lineInner.appendChild(wordOuter.firstChild as Node);
      if (i < lineWords.length - 1) lineInner.appendChild(document.createTextNode(' '));
    });
    lineWrap.appendChild(lineInner);
    el.appendChild(lineWrap);
    inner.push(lineInner);
  }
  return { targets: inner, revert: () => revert(el) };
}

export function splitToChars(el: HTMLElement): SplitResult {
  storeOriginal(el);
  const text = el.textContent ?? '';
  el.innerHTML = '';
  const inner: HTMLElement[] = [];
  for (const ch of text) {
    if (ch === ' ') {
      el.appendChild(document.createTextNode(' '));
      continue;
    }
    const outer = document.createElement('span');
    outer.className = 'split-word';
    outer.setAttribute('aria-hidden', 'true');
    const span = document.createElement('span');
    span.textContent = ch;
    outer.appendChild(span);
    el.appendChild(outer);
    inner.push(span);
  }
  return { targets: inner, revert: () => revert(el) };
}

function revert(el: HTMLElement) {
  if (el.dataset.origHtml !== undefined) {
    el.innerHTML = el.dataset.origHtml;
    delete el.dataset.origHtml;
  }
}

/** Build a reveal timeline for split targets (lines/words/chars). */
export function revealTimeline(
  targets: HTMLElement[],
  opts: { stagger?: number; duration?: number; delay?: number; y?: string; from?: gsap.TweenVars } = {},
): gsap.core.Timeline {
  const tl = gsap.timeline();
  tl.from(targets, {
    yPercent: 118,
    opacity: 0,
    duration: opts.duration ?? 0.9,
    ease: 'power4.out',
    stagger: opts.stagger ?? 0.08,
    delay: opts.delay ?? 0,
    ...opts.from,
  });
  return tl;
}
